import { Button, Title, Tooltip, Chip } from '@cognite/cogs.js';
import styled from 'styled-components/macro';
import { Table } from 'antd';
import { formatDateRecentTime } from 'utils/misc';
import {
  checkRunHasValidData,
  getValidInstancesPercentage,
} from 'utils/data-validation';
import { RuleRunStatus, RunStatus } from 'types/dt-graphql-apollo-v2';
import { LabelListDisplay } from 'components';
import { PAGINATION_PAGE_SIZE } from 'utils/config';

type RuleRunValidityProps = {
  runStatus: RuleRunStatus;
  showPercentage?: boolean;
};

export const RuleRunValidity = ({
  runStatus,
  showPercentage = true,
}: RuleRunValidityProps) => {
  const percentage = getValidInstancesPercentage(
    runStatus.totalValid!,
    runStatus.totalFailed!
  );

  const color = percentage < 50 && percentage >= 0 ? 'var(--cogs-danger)' : '';
  if (runStatus.status === RunStatus.Error) {
    return (
      <Tooltip
        maxWidth="50vw"
        disabled={!runStatus.message}
        content={runStatus.message}
      >
        <Chip type="danger" label="Failed to run" icon="Warning" />
      </Tooltip>
    );
  }

  if (runStatus.status === RunStatus.InProgress) {
    return (
      <>
        <Chip type="neutral" label="In progress" icon="Loader" />
      </>
    );
  }

  if (!checkRunHasValidData(runStatus.totalValid, runStatus.totalFailed)) {
    return (
      <>
        <Chip type="neutral" label="No data to validate." icon="HelpFilled" />
      </>
    );
  }

  const renderText = showPercentage
    ? `${percentage.toFixed(1)}%`
    : runStatus.totalFailed || 0;
  return (
    <Tooltip
      content={`${runStatus.totalValid} of ${
        (runStatus.totalValid || 0) + (runStatus.totalFailed || 0)
      }`}
    >
      <ColoredDiv color={color}>{renderText}</ColoredDiv>
    </Tooltip>
  );
};

export const RunsHistory = ({
  runHistory,
  selectedRun,
  setSelectedRun,
  showPagination = false,
  title,
  timeStamp,
}: {
  runHistory: RuleRunStatus[];
  selectedRun?: RuleRunStatus;
  setSelectedRun: (run: RuleRunStatus) => void;
  showPagination?: boolean;
  title: string;
  timeStamp?: string;
}) => {
  if (!runHistory.length) return null;

  const getLastRunColumn = (status: string, runAt: string) => {
    switch (status) {
      case RunStatus.Error: {
        return (
          <Chip
            type="danger"
            label={`Failed: ${formatDateRecentTime(runAt)}`}
            size="small"
          />
        );
      }
      default: {
        return <span>{formatDateRecentTime(runAt)}</span>;
      }
    }
  };

  const runHistoryColumns = [
    {
      title: 'Last run',
      dataIndex: 'runAt',
      key: 'runAt',
      render: (runAt: string, record: RuleRunStatus) =>
        getLastRunColumn(record.status, runAt),
    },
    {
      title: 'Rule name',
      dataIndex: 'rule',
      key: 'name',
      render: (rule: RuleRunStatus['rule']) => rule?.name,
    },
    {
      title: 'Validity',
      dataIndex: ['totalFailed', 'totalValid'],
      render: (_: any, record: RuleRunStatus) => (
        <RuleRunValidity runStatus={record} />
      ),
    },
    {
      title: 'Labels',
      dataIndex: 'rule',
      key: 'labels',
      render: (rule: RuleRunStatus['rule']) => (
        <LabelListDisplay
          labelSize="medium"
          labels={rule?.tags?.map((tag) => ({ text: tag, key: tag }))}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'status',
      key: 'action',
      render: (status: RunStatus) =>
        status !== RunStatus.Error && (
          <Button
            type="ghost"
            icon="ArrowRight"
            size="small"
            aria-label={`Open run with status ${status}`}
          />
        ),
    },
  ];

  return (
    <Group>
      <StyledTitleTabPane level={5}>
        {title} {timeStamp && <small>{timeStamp}</small>}
      </StyledTitleTabPane>
      <StyledTable<RuleRunStatus>
        rowClassName={(record) =>
          `${record === selectedRun ? 'selected-run' : 'none'} ${
            // For now rowClassName doesn't return a correct record object
            // In future with new cogs hopefully it will return correct record
            // type and then we wouldn't need typecasting here
            record.status === RunStatus.Error && !record.message
              ? 'disabled'
              : ''
          }
          `
        }
        onRow={(record) => ({
          onClick: () => setSelectedRun(record),
        })}
        columns={runHistoryColumns}
        dataSource={runHistory}
        size="small"
        pagination={
          showPagination
            ? {
                defaultPageSize: PAGINATION_PAGE_SIZE,
                hideOnSinglePage: true,
              }
            : false
        }
      />
    </Group>
  );
};

const ColoredDiv = styled.div<{ color: string }>`
  color: ${({ color }) => color};
`;

const Group = styled.div`
  width: 100%;
  padding: 0.5rem;
`;

const StyledTitleTabPane = styled(Title)`
  margin-bottom: 0.5rem;
  small {
    font-size: 12px;
    font-weight: normal;
  }
`;

const StyledTable = styled(Table)`
  .ant-table-small .ant-table-thead > tr > th {
    background-color: transparent;
  }

  .ant-table table {
    border-spacing: 0 0.2rem;
  }

  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: unset;
  }

  .ant-table-row {
    &:hover {
      cursor: pointer;
      background: var(--cogs-bg-accent);
    }
  }
` as typeof Table;
